import React, { useState, useMemo } from 'react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Container,
  TableContainer,
  Grid,
  Typography,
  Button,
  Box,
  Tabs,
  Tab,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import SearchNotFound from 'src/components/SearchNotFound';
import Iconify from 'src/components/Iconify';
import CustomPagination from 'src/components/CustomPagination';
import { ToastNotifications } from 'src/components';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { loanService } from 'src/services/loan.service';
import { studentService } from 'src/services/student.service';
import {
  calculateLoanProgress,
  formatScholarDollars,
  AmortizationInstallment,
} from 'src/utils/loanCalculator';
import { invalidateStudentFinancialQueries } from 'src/utils/queryInvalidation';
import { Loan } from 'src/types/loan.types';

export default function StudentLoanRequests() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useHeaderBreadcrumbs({
    breadcrumbs: [
      { label: 'Loans & Credit Hub', path: '/student/loan-requests' },
      { label: 'My Loan Obligations' },
    ],
  });

  const [tabIndex, setTabIndex] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [searchText, setSearchText] = useState('');

  // Modals state
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  // Payment Form state
  const [paymentOption, setPaymentOption] = useState<'installment' | 'full' | 'custom'>('installment');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // 1. Fetch Student Info
  const { data: studentId } = useQuery({
    queryKey: ['student-id', user?.id],
    queryFn: () => studentService.getStudentByUserId(user?.id as string),
    enabled: !!user?.id,
  });

  const { data: student } = useQuery({
    queryKey: ['student-profile', studentId],
    queryFn: () => studentService.getStudentById(studentId as string),
    enabled: !!studentId,
  });

  // 2. Fetch Student Loans
  const { data: loansData, isLoading } = useQuery({
    queryKey: ['student-loans', studentId, page, rowsPerPage, tabIndex, searchText],
    queryFn: () =>
      loanService.getLoans({
        studentId: studentId as string,
        status: tabIndex === 'ALL' ? undefined : tabIndex,
        search: searchText,
        page,
        limit: rowsPerPage,
      }),
    enabled: !!studentId,
  });

  // 3. Fetch All Loans (for Tab counts)
  const { data: allLoansData } = useQuery({
    queryKey: ['student-all-loans-counts', studentId],
    queryFn: () => loanService.getLoans({ studentId: studentId as string, limit: 100 }),
    enabled: !!studentId,
  });

  const loans = loansData?.data || [];
  const allLoans = allLoansData?.data || [];
  const checkingBalance = Number(student?.balance) || 0;

  // Counts for tabs
  const counts = useMemo(() => {
    return {
      all: allLoans.length,
      active: allLoans.filter((l) => l.status === 'Approved' || l.status === 'Overdue').length,
      pending: allLoans.filter((l) => l.status === 'Pending').length,
      paid: allLoans.filter((l) => l.status === 'Paid').length,
      denied: allLoans.filter((l) => l.status === 'Denied').length,
    };
  }, [allLoans]);

  // Active Loan for the Hero Card
  const activeLoan = useMemo(() => {
    return loans.find((l) => l.status === 'Approved' || l.status === 'Overdue') || null;
  }, [loans]);

  const activeLoanProgress = useMemo(() => {
    return activeLoan ? calculateLoanProgress(activeLoan) : null;
  }, [activeLoan]);

  // Selected Loan Progress for Payment Modal
  const selectedLoanProgress = useMemo(() => {
    return selectedLoan ? calculateLoanProgress(selectedLoan) : null;
  }, [selectedLoan]);

  // Handle Payment Execution
  const handleExecutePayment = async () => {
    if (!selectedLoan || !selectedLoanProgress || !studentId) return;

    let amountToPay = 0;
    if (paymentOption === 'installment') {
      amountToPay = Math.min(selectedLoanProgress.nextInstallmentAmount, selectedLoanProgress.remainingBalance);
    } else if (paymentOption === 'full') {
      amountToPay = selectedLoanProgress.remainingBalance;
    } else {
      amountToPay = parseFloat(customAmount);
    }

    if (isNaN(amountToPay) || amountToPay <= 0) {
      ToastNotifications.error('Please enter a valid positive payment amount.');
      return;
    }

    if (amountToPay > checkingBalance) {
      ToastNotifications.error(
        `Insufficient Scholar Dollars. Your wallet balance is ${formatScholarDollars(checkingBalance)}.`
      );
      return;
    }

    setPaymentLoading(true);
    try {
      await loanService.repayLoan({
        loanId: selectedLoan.id,
        studentId,
        amount: amountToPay,
        paymentMethod: 'manual_student',
        note: 'Student manual payment from portal',
      });

      ToastNotifications.success(`Successfully paid ${formatScholarDollars(amountToPay)} towards your loan!`);
      setOpenPaymentModal(false);
      setCustomAmount('');

      await invalidateStudentFinancialQueries(queryClient, {
        extraKeys: [['student-loans'], ['loans'], ['student-profile'], ['student-all-loans-counts']],
      });
    } catch (err: any) {
      ToastNotifications.error(err?.message || 'Payment failed.');
      console.error('[StudentLoanRequests.handleExecutePayment]:', err);
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle Loan Cancellation / Deletion
  const handleDeleteLoan = async () => {
    if (!selectedLoan) return;
    try {
      await loanService.deleteLoan(selectedLoan.id);
      ToastNotifications.success('Loan request cancelled.');
      setOpenDeleteModal(false);
      await invalidateStudentFinancialQueries(queryClient, {
        extraKeys: [['student-loans'], ['loans'], ['student-all-loans-counts']],
      });
    } catch (err: any) {
      ToastNotifications.error(err?.message || 'Failed to cancel loan request.');
    }
  };

  return (
    <Page title="My Loans">
      <Container maxWidth={false} sx={{ py: 2.5, px: { xs: 2, md: 3 } }}>
        {/* Top Header Row with Icon on the side */}
        <Box sx={{ mb: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon="solar:hand-money-bold-duotone" width={24} height={24} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', fontSize: '1.375rem' }}>
                  My Loans & Credit Hub
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                  Track your borrowing history, simulate repayment schedules, and make on-time payments.
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="solar:hand-money-bold-duotone" width={18} />}
              onClick={() => navigate('/student/new-loan-request')}
              sx={{
                height: 38,
                px: 2.25,
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
              }}
            >
              + Request Loan
            </Button>
          </Stack>
        </Box>

        {/* Hero Card: Active Loan Spotlight OR Clean Zero-Debt Banner */}
        {activeLoan && activeLoanProgress ? (
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              mb: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.25),
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} md={7}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.75,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Iconify icon="solar:hand-money-bold-duotone" width={22} />
                  </Box>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                        Active Loan: {activeLoan.reason}
                      </Typography>
                      <Chip
                        icon={
                          <Iconify
                            icon={activeLoan.status === 'Overdue' ? 'solar:danger-triangle-bold-duotone' : 'solar:check-circle-bold-duotone'}
                            width={13}
                          />
                        }
                        label={activeLoan.status}
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          bgcolor: activeLoan.status === 'Overdue' ? 'error.lighter' : 'success.lighter',
                          color: activeLoan.status === 'Overdue' ? 'error.main' : 'success.main',
                        }}
                      />
                    </Stack>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                      Approved {moment(activeLoan.createdat).format('MMM D, YYYY')} · {activeLoan.term_months} Months Term @ {activeLoan.interest_rate}% APR
                    </Typography>
                  </Box>
                </Stack>

                {/* Progress Bar */}
                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Paid: {formatScholarDollars(activeLoanProgress.paidAmount)} ({activeLoanProgress.percentPaid}%)
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Remaining: {formatScholarDollars(activeLoanProgress.remainingBalance)}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={activeLoanProgress.percentPaid}
                    sx={{
                      height: 7,
                      borderRadius: 3.5,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': { bgcolor: 'primary.main', borderRadius: 3.5 },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                        Next Scheduled Installment
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', my: 0.25, fontSize: '1.2rem' }}>
                        {formatScholarDollars(activeLoanProgress.nextInstallmentAmount)}
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<Iconify icon="solar:card-recive-bold-duotone" width={16} />}
                      onClick={() => {
                        setSelectedLoan(activeLoan);
                        setOpenPaymentModal(true);
                      }}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        height: 36,
                      }}
                    >
                      Make Payment
                    </Button>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Due Date: {activeLoanProgress.nextInstallmentDueDate ? moment(activeLoanProgress.nextInstallmentDueDate).format('MMM D, YYYY') : 'End of Term'}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedLoan(activeLoan);
                        setOpenScheduleModal(true);
                      }}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', p: 0 }}
                    >
                      View Schedule
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Card>
        ) : (
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              mb: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.75,
                  bgcolor: 'success.lighter',
                  color: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon="solar:shield-check-bold-duotone" width={22} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                  Zero Outstanding Loan Obligations
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                  You have no active loans. If you need funds for a project, store inventory, or financial challenge, click "+ Request Loan" above.
                </Typography>
              </Box>
            </Stack>
          </Card>
        )}

        {/* Loan History Table Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {/* Tabs Filter Bar */}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} sx={{ px: 2, py: 1 }}>
              <Tabs
                value={tabIndex}
                onChange={(_, val) => {
                  setTabIndex(val);
                  setPage(1);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 42,
                  '& .MuiTabs-indicator': {
                    bgcolor: 'primary.main',
                    height: 2.5,
                    borderRadius: 1.25,
                  },
                  '& .MuiTab-root': {
                    minHeight: 42,
                    py: 0.5,
                    px: 1.5,
                    borderRadius: 1.5,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    color: 'text.secondary',
                    minWidth: 'auto',
                    mr: 0.5,
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  },
                }}
              >
                <Tab
                  value="ALL"
                  icon={<Iconify icon="solar:layers-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>All Loans</span>
                      <Chip
                        label={counts.all}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'ALL' ? 'primary.main' : 'action.selected',
                          color: tabIndex === 'ALL' ? 'primary.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value="Approved"
                  icon={<Iconify icon="solar:hand-money-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>Active</span>
                      <Chip
                        label={counts.active}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'Approved' ? 'primary.main' : 'action.selected',
                          color: tabIndex === 'Approved' ? 'primary.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value="Pending"
                  icon={<Iconify icon="solar:hourglass-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>Pending</span>
                      <Chip
                        label={counts.pending}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'Pending' ? 'warning.main' : 'action.selected',
                          color: tabIndex === 'Pending' ? 'warning.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value="Paid"
                  icon={<Iconify icon="solar:verified-check-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>Paid</span>
                      <Chip
                        label={counts.paid}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'Paid' ? 'primary.main' : 'action.selected',
                          color: tabIndex === 'Paid' ? 'primary.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value="Denied"
                  icon={<Iconify icon="solar:close-circle-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>Declined</span>
                      <Chip
                        label={counts.denied}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'Denied' ? 'error.main' : 'action.selected',
                          color: tabIndex === 'Denied' ? 'error.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
              </Tabs>

              <Box sx={{ width: { xs: '100%', md: 220 } }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search reason..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setPage(1);
                  }}
                  InputProps={{
                    startAdornment: <Iconify icon="solar:magnifer-linear" width={15} sx={{ color: 'text.secondary', mr: 0.75 }} />,
                  }}
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                    '& .MuiOutlinedInput-input': { py: 0.65, fontSize: '0.8125rem' },
                  }}
                />
              </Box>
            </Stack>
          </Box>

          {/* Table */}
          <Scrollbar>
            <TableContainer>
              <Table sx={{ minWidth: 850 }}>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ width: '26%', py: 1.25, px: 2, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Loan Purpose
                    </TableCell>
                    <TableCell sx={{ width: '13%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Applied Date
                    </TableCell>
                    <TableCell sx={{ width: '11%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Principal
                    </TableCell>
                    <TableCell sx={{ width: '13%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Term & Rate
                    </TableCell>
                    <TableCell sx={{ width: '13%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Installment
                    </TableCell>
                    <TableCell sx={{ width: '10%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ width: '14%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Progress
                    </TableCell>
                    <TableCell align="right" sx={{ width: '6%', py: 1.25, px: 2, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={26} />
                      </TableCell>
                    </TableRow>
                  ) : loans.length > 0 ? (
                    loans.map((row) => {
                      const progress = calculateLoanProgress(row);
                      const isPending = row.status === 'Pending';
                      const isApproved = row.status === 'Approved' || row.status === 'Overdue';
                      const isPaid = row.status === 'Paid';

                      return (
                        <TableRow hover key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          {/* Loan Purpose */}
                          <TableCell sx={{ py: 1.25, px: 2, verticalAlign: 'middle' }}>
                            <Tooltip title={row.reason} arrow placement="top-start">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem', maxWidth: 220 }} noWrap>
                                {row.reason || 'Educational Microloan'}
                              </Typography>
                            </Tooltip>
                            {row.extendedproperties?.counterOffer && (
                              <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', fontWeight: 600, fontSize: '0.6875rem' }}>
                                Counter-offered by educator
                              </Typography>
                            )}
                          </TableCell>

                          {/* Applied Date */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                              {moment(row.createdat).format('MMM D, YYYY')}
                            </Typography>
                          </TableCell>

                          {/* Principal */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
                              {formatScholarDollars(row.amount)}
                            </Typography>
                          </TableCell>

                          {/* Term & Rate */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                              {row.term_months} Mo @ {row.interest_rate}%
                            </Typography>
                          </TableCell>

                          {/* Installment */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8125rem' }}>
                              {formatScholarDollars(row.monthly_payment)} / mo
                            </Typography>
                          </TableCell>

                          {/* Status */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Chip
                              icon={
                                <Iconify
                                  icon={
                                    row.status === 'Approved'
                                      ? 'solar:check-circle-bold-duotone'
                                      : row.status === 'Paid'
                                      ? 'solar:verified-check-bold-duotone'
                                      : row.status === 'Denied'
                                      ? 'solar:close-circle-bold-duotone'
                                      : 'solar:hourglass-bold-duotone'
                                  }
                                  width={13}
                                />
                              }
                              label={row.status}
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 600,
                                fontSize: '0.725rem',
                                bgcolor:
                                  row.status === 'Approved'
                                    ? 'success.lighter'
                                    : row.status === 'Paid'
                                    ? 'info.lighter'
                                    : row.status === 'Denied'
                                    ? 'error.lighter'
                                    : 'warning.lighter',
                                color:
                                  row.status === 'Approved'
                                    ? 'success.main'
                                    : row.status === 'Paid'
                                    ? 'info.main'
                                    : row.status === 'Denied'
                                    ? 'error.main'
                                    : 'warning.main',
                              }}
                            />
                          </TableCell>

                          {/* Repayment Progress */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle' }}>
                            <Box sx={{ minWidth: 100 }}>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem' }}>
                                  {formatScholarDollars(progress.paidAmount)} / {formatScholarDollars(progress.totalAmount)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.6875rem' }}>
                                  {progress.percentPaid}%
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={progress.percentPaid}
                                sx={{
                                  height: 5,
                                  borderRadius: 2.5,
                                  bgcolor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: isPaid ? 'info.main' : 'primary.main',
                                    borderRadius: 2.5,
                                  },
                                }}
                              />
                            </Box>
                          </TableCell>

                          {/* Actions */}
                          <TableCell align="right" sx={{ py: 1.25, px: 2, verticalAlign: 'middle' }}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              {isApproved && (
                                <Tooltip title="Make a Payment" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedLoan(row);
                                      setOpenPaymentModal(true);
                                    }}
                                    sx={{ width: 28, height: 28, color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                                  >
                                    <Iconify icon="solar:card-recive-bold-duotone" width={15} />
                                  </IconButton>
                                </Tooltip>
                              )}

                              <Tooltip title="View Details" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/student/new-loan-request/${row.id}?mode=view`)}
                                  sx={{ width: 28, height: 28, color: 'text.secondary' }}
                                >
                                  <Iconify icon="solar:eye-bold-duotone" width={15} />
                                </IconButton>
                              </Tooltip>

                              {row.extendedproperties?.amortizationSchedule && (
                                <Tooltip title="Repayment Schedule" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedLoan(row);
                                      setOpenScheduleModal(true);
                                    }}
                                    sx={{ width: 28, height: 28, color: 'info.main' }}
                                  >
                                    <Iconify icon="solar:calendar-date-bold-duotone" width={15} />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {isPending && (
                                <Tooltip title="Cancel Request" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedLoan(row);
                                      setOpenDeleteModal(true);
                                    }}
                                    sx={{ width: 28, height: 28, color: 'error.main' }}
                                  >
                                    <Iconify icon="solar:trash-bin-trash-bold-duotone" width={15} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <SearchNotFound message="No loan requests found." />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          {loansData && loansData.count > rowsPerPage && (
            <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <CustomPagination
                count={loansData.count}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(p) => setPage(p)}
                onRowsPerPageChange={(r) => {
                  setRowsPerPage(r);
                  setPage(1);
                }}
              />
            </Box>
          )}
        </Card>
      </Container>

      {/* 1. MAKE PAYMENT MODAL */}
      <Dialog
        open={openPaymentModal}
        onClose={() => !paymentLoading && setOpenPaymentModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify icon="solar:card-recive-bold-duotone" width={20} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                Make Loan Payment
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {selectedLoan?.reason}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {/* Balance Overview */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Wallet Balance:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {formatScholarDollars(checkingBalance)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Remaining Loan Debt:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {formatScholarDollars(selectedLoanProgress?.remainingBalance || 0)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Payment Option Selector */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontSize: '0.8125rem' }}>
            Select Payment Option:
          </Typography>

          <RadioGroup
            value={paymentOption}
            onChange={(e) => setPaymentOption(e.target.value as any)}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1.25,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: paymentOption === 'installment' ? 'primary.main' : 'divider',
                bgcolor: paymentOption === 'installment' ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
              }}
            >
              <FormControlLabel
                value="installment"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Pay Next Installment: {formatScholarDollars(selectedLoanProgress?.nextInstallmentAmount || 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Regular scheduled installment for this period.
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1.25,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: paymentOption === 'full' ? 'primary.main' : 'divider',
                bgcolor: paymentOption === 'full' ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
              }}
            >
              <FormControlLabel
                value="full"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Pay Full Remaining Balance: {formatScholarDollars(selectedLoanProgress?.remainingBalance || 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Completely settle and pay off this loan today.
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: paymentOption === 'custom' ? 'primary.main' : 'divider',
                bgcolor: paymentOption === 'custom' ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
              }}
            >
              <FormControlLabel
                value="custom"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Pay Custom Amount
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Enter any custom amount up to your remaining balance.
                    </Typography>
                  </Box>
                }
              />
            </Paper>
          </RadioGroup>

          {paymentOption === 'custom' && (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Custom Payment Amount ($D)"
                placeholder="e.g. 25.00"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                inputProps={{ min: 1, max: selectedLoanProgress?.remainingBalance }}
                sx={{ bgcolor: 'background.paper' }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenPaymentModal(false)}
            disabled={paymentLoading}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecutePayment}
            disabled={paymentLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
            }}
          >
            {paymentLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. REPAYMENT SCHEDULE MODAL */}
      <Dialog
        open={openScheduleModal}
        onClose={() => setOpenScheduleModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Amortization & Repayment Schedule
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.8125rem' }}>
            Full schedule of monthly installments, interest allocation, and payment status for this loan.
          </Typography>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>#</TableCell>
                  <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Due Date</TableCell>
                  <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Installment</TableCell>
                  <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Principal</TableCell>
                  <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Interest</TableCell>
                  <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Remaining</TableCell>
                  <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedLoan?.extendedproperties?.amortizationSchedule || []).map((item: AmortizationInstallment) => (
                  <TableRow key={item.installmentNumber} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 700 }}>{item.installmentNumber}</TableCell>
                    <TableCell>{moment(item.dueDate).format('MMM D, YYYY')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>{formatScholarDollars(item.amount)}</TableCell>
                    <TableCell>{formatScholarDollars(item.principal)}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{formatScholarDollars(item.interest)}</TableCell>
                    <TableCell>{formatScholarDollars(item.remainingBalance)}</TableCell>
                    <TableCell>
                      <Chip
                        icon={<Iconify icon={item.status === 'paid' ? 'solar:check-circle-bold-duotone' : 'solar:hourglass-bold-duotone'} width={12} />}
                        label={item.status === 'paid' ? 'Paid' : 'Pending'}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          bgcolor: item.status === 'paid' ? 'success.lighter' : 'warning.lighter',
                          color: item.status === 'paid' ? 'success.main' : 'warning.main',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" color="primary" onClick={() => setOpenScheduleModal(false)} sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3. CONFIRM DELETE MODAL */}
      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Loan Application?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this pending loan application? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDeleteModal(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Keep Application
          </Button>
          <Button onClick={handleDeleteLoan} color="error" variant="contained" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}>
            Cancel Loan Request
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
